import { useState, useEffect } from "react";
import { adminFetch } from "../../lib/api";

const ALL_EVENTS = [
  { value: "status_change", label: "Status Change" },
  { value: "incident_created", label: "Incident Created" },
  { value: "incident_resolved", label: "Incident Resolved" },
  { value: "maintenance_starting", label: "Maintenance Starting" },
];

interface WebhookItem { id: string; url: string; events: string[]; is_active: boolean; created_at: string; created_by: string; }

export function WebhookConfigPage() {
  const [webhooks, setWebhooks] = useState<WebhookItem[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newUrl, setNewUrl] = useState("");
  const [selEvents, setSelEvents] = useState<string[]>([]);
  const [shownSecret, setShownSecret] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  async function load() {
    const r = await adminFetch("/api/v1/admin/webhooks/");
    if (r.ok) setWebhooks(await r.json());
  }

  useEffect(() => { load(); }, []);

  function toggleEvent(v: string) { setSelEvents(p => p.includes(v) ? p.filter(x => x !== v) : [...p, v]); }

  async function create(e: React.FormEvent) {
    e.preventDefault(); setError(""); setSubmitting(true);
    try {
      const r = await adminFetch("/api/v1/admin/webhooks/", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: newUrl, events: selEvents }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      if (d.secret) setShownSecret(d.secret);
      setShowCreate(false); setNewUrl(""); setSelEvents([]);
      await load();
    } catch { setError("Failed to create webhook."); }
    finally { setSubmitting(false); }
  }

  async function del(id: string) {
    if (!confirm("Deactivate this webhook?")) return;
    await adminFetch(`/api/v1/admin/webhooks/${id}/`, { method: "DELETE" });
    await load();
  }

  return (
    <div className="ac-page">
      <div className="ac-page-header">
        <div className="ac-page-header__left">
          <h1 className="ac-page-title">Webhooks</h1>
          <p className="ac-page-subtitle">Configure outbound webhook notifications for platform events</p>
        </div>
        <div className="ac-page-header__actions">
          <button type="button" className="aptean-btn aptean-btn--primary" onClick={() => setShowCreate(true)}>+ Add Webhook</button>
        </div>
      </div>

      <div className="ac-table-wrap">
        <table className="ac-table">
          <thead><tr><th style={{ width: "40%" }}>Endpoint URL</th><th>Events</th><th>Status</th><th>Created</th><th className="ac-th--right">Actions</th></tr></thead>
          <tbody>
            {webhooks.length === 0 ? (
              <tr><td colSpan={5}><div className="ac-empty"><div className="ac-empty__icon">&#8599;</div><div className="ac-empty__title">No webhooks configured</div><div className="ac-empty__body">Add a webhook to receive real-time event notifications.</div></div></td></tr>
            ) : webhooks.map(w => (
              <tr key={w.id}>
                <td className="ac-td--mono" style={{ fontSize: "0.8125rem", wordBreak: "break-all" }}>{w.url}</td>
                <td style={{ fontSize: "0.8125rem" }}>{w.events.map(ev => ALL_EVENTS.find(e => e.value === ev)?.label ?? ev).join(", ") || "—"}</td>
                <td><span className={`ac-badge ${w.is_active ? "ac-badge--operational" : "ac-badge--neutral"}`}>{w.is_active ? "Active" : "Inactive"}</span></td>
                <td className="ac-td--muted">{new Date(w.created_at).toLocaleDateString()}</td>
                <td className="ac-td--right ac-td--actions">
                  <button type="button" className="aptean-btn aptean-btn--ghost" style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem" }} onClick={() => del(w.id)}>Deactivate</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <div className="ac-overlay" onClick={e => e.target === e.currentTarget && setShowCreate(false)}>
          <div className="ac-dialog">
            <div className="ac-dialog__header">
              <h2 className="ac-dialog__title">Add Webhook</h2>
              <button type="button" className="ac-dialog__close" onClick={() => setShowCreate(false)}>&#10005;</button>
            </div>
            <form onSubmit={create}>
              <div className="ac-dialog__body">
                {error && <div className="ac-alert ac-alert--error">{error}</div>}
                <div className="ac-field">
                  <label>Endpoint URL *</label>
                  <input type="url" className="aptean-input" value={newUrl} onChange={e => setNewUrl(e.target.value)} placeholder="https://example.com/hook" required />
                </div>
                <div className="ac-field">
                  <label>Events to receive</label>
                  <div className="ac-checkbox-list">
                    {ALL_EVENTS.map(({ value, label }) => (
                      <label key={value} className="ac-checkbox-item">
                        <input type="checkbox" checked={selEvents.includes(value)} onChange={() => toggleEvent(value)} />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="ac-dialog__footer">
                <button type="button" className="aptean-btn aptean-btn--ghost" onClick={() => setShowCreate(false)}>Cancel</button>
                <button type="submit" className="aptean-btn aptean-btn--primary" disabled={submitting}>{submitting ? "Adding..." : "Add Webhook"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {shownSecret && (
        <div className="ac-overlay">
          <div className="ac-dialog">
            <div className="ac-dialog__header">
              <h2 className="ac-dialog__title">Save your webhook secret</h2>
            </div>
            <div className="ac-dialog__body">
              <div className="ac-alert ac-alert--warning">This secret is shown only once. Copy it now — you cannot retrieve it later.</div>
              <div className="ac-secret-box">{shownSecret}</div>
            </div>
            <div className="ac-dialog__footer">
              <button type="button" className="aptean-btn aptean-btn--primary" onClick={() => setShownSecret(null)}>I've saved it</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
